/**
 * apiErrors — backend error-code constants and helpers.
 *
 * The backend returns errors as `{ success: false, error: { code, message } }`
 * where `code` carries either an HTTP status (number) or a machine code
 * (string, e.g. `BUYER_FORBIDDEN`). The shared apiClient surfaces this body
 * as-is, so call sites can branch on `err.error.code`. This module is the
 * single source of truth for those codes — never hardcode the strings.
 */
import type { ApiErrorResponse, ApiResponse } from '../services/apiClient';

export const ERR = {
  /** Buyer JWT hit a creator-only write endpoint (archives, products, store, etc.). */
  BUYER_FORBIDDEN: 'BUYER_FORBIDDEN',
  /** Tried to start a DM with a buyer designerId. */
  BUYER_NOT_DM_TARGET: 'BUYER_NOT_DM_TARGET',

  // Transport-level codes. These never come from the backend — the apiClient
  // synthesises them when a request never reached it (or never came back), so
  // the UI can tell "your network died" apart from "the server said no".
  /** The device has no internet, or the request never reached the server. */
  NETWORK_OFFLINE: 'NETWORK_OFFLINE',
  /** The request ran past its deadline, usually a slow uplink on a big upload. */
  NETWORK_TIMEOUT: 'NETWORK_TIMEOUT',
  /** The caller aborted the request on purpose (user pressed Cancel). */
  REQUEST_CANCELLED: 'REQUEST_CANCELLED',
} as const;

export type ApiErrorCode = (typeof ERR)[keyof typeof ERR];

const codeOf = (e: unknown): string | number | undefined => {
  if (!e || typeof e !== 'object') return undefined;
  // Shape A: a parsed ApiErrorResponse (from apiClient.get/post/...)
  const asApi = e as Partial<ApiErrorResponse>;
  if (asApi.error && (asApi.success === false || 'code' in asApi.error)) {
    return asApi.error.code as string | number | undefined;
  }
  // Shape B: an Error subclass we threw with `.code` attached.
  const asErr = e as { code?: string | number };
  if (typeof asErr.code === 'string' || typeof asErr.code === 'number') {
    return asErr.code;
  }
  return undefined;
};

const messageOf = (e: unknown): string | undefined => {
  if (!e || typeof e !== 'object') return undefined;
  const asApi = e as Partial<ApiErrorResponse>;
  if (asApi.error?.message) return asApi.error.message;
  const asErr = e as { message?: string };
  return asErr.message;
};

/**
 * True when the error reports a specific machine code. Tolerant to whichever
 * layer surfaces it: ApiErrorResponse (apiClient), thrown Error with `.code`,
 * or a custom error class (e.g. ApiCallError below).
 */
export const isApiErrorCode = (e: unknown, code: ApiErrorCode): boolean => {
  return codeOf(e) === code;
};

export const isBuyerForbidden = (e: unknown): boolean =>
  isApiErrorCode(e, ERR.BUYER_FORBIDDEN);

export const isBuyerNotDmTarget = (e: unknown): boolean =>
  isApiErrorCode(e, ERR.BUYER_NOT_DM_TARGET);

/**
 * Custom error class for API failures that need to preserve `code` across
 * a `throw`. The plain apiClient response keeps `error.code`, but call sites
 * that wrap responses in `if (!res.success) throw new Error(...)` lose it.
 * Throw `ApiCallError` instead so downstream `catch` blocks can inspect
 * `err.code` to drive UX (e.g. toast on BUYER_NOT_DM_TARGET).
 */
export class ApiCallError extends Error {
  readonly code: string | number;
  readonly details?: unknown;
  constructor(code: string | number, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiCallError';
    this.code = code;
    this.details = details;
  }

  /** Build from an apiClient ApiErrorResponse, preserving code/message. */
  static fromResponse(res: ApiErrorResponse): ApiCallError {
    return new ApiCallError(
      res.error?.code ?? 'UNKNOWN',
      res.error?.message ?? 'Request failed',
      res.error?.details,
    );
  }
}

/**
 * Unwrap an apiClient ApiResponse: return `data` on success, throw an
 * `ApiCallError` (preserving code) on failure. Keeps call sites tidy without
 * losing the machine code that drives buyer-aware UI branches.
 */
export const unwrap = <T>(res: ApiResponse<T>): T => {
  if (res.success) return res.data;
  throw ApiCallError.fromResponse(res);
};

/** Best-effort human message for any error shape. */
export const errorMessage = (e: unknown, fallback = 'Something went wrong'): string =>
  messageOf(e) ?? fallback;
